import Vue from 'vue'
import { sortStatusesByPosition, reorderStatusList } from '@modules/tarefas/utils/statusOrdering'
import { _findFolderNode } from './mutations-internal'

export function SET_TAGS(state, tags) {
  state.tags = tags || []
  state.tagsLastUpdate = Date.now()
}

// --- Per-folder custom statuses (customizar-status-tarefas) -------------
// All mutations are keyed by `folderId` and only touch that folder's bucket.
// `Vue.set` is used whenever a folder key may be new so Vue 2 reactivity
// tracks the added property on the plain object maps. Lists are kept sorted
// by `position` (ASC, null/undefined last) so consumers never re-sort.

export function SET_FOLDER_STATUSES(state, { folderId, statuses } = {}) {
  if (!folderId) return
  Vue.set(state.statusesByFolder, folderId, sortStatusesByPosition(statuses))
  Vue.set(state.statusesLastUpdate, folderId, Date.now())
}

export function SET_FOLDER_STATUSES_LOADING(state, { folderId, value } = {}) {
  if (!folderId) return
  Vue.set(state.statusesLoading, folderId, !!value)
}

export function ADD_FOLDER_STATUS(state, { folderId, status } = {}) {
  if (!folderId || !status) return
  const current = Array.isArray(state.statusesByFolder[folderId])
    ? state.statusesByFolder[folderId].slice()
    : []
  // Defensive: when a status is created as the new default, unset the
  // previous default in the same folder (mirrors the backend mutex).
  const next = status.isDefault === true
    ? current.map(function (s) { return { ...s, isDefault: false } })
    : current
  next.push(status)
  Vue.set(state.statusesByFolder, folderId, sortStatusesByPosition(next))
}

export function UPDATE_FOLDER_STATUS(state, { folderId, status } = {}) {
  if (!folderId || !status || status.id == null) return
  const current = Array.isArray(state.statusesByFolder[folderId])
    ? state.statusesByFolder[folderId]
    : []
  const next = current.map(function (s) {
    if (!s) return s
    if (s.id === status.id) return { ...s, ...status }
    // Enforce the single-default mutex within the folder when the updated
    // status becomes the new default.
    if (status.isDefault === true) return { ...s, isDefault: false }
    return s
  })
  Vue.set(state.statusesByFolder, folderId, sortStatusesByPosition(next))
}

export function REMOVE_FOLDER_STATUS(state, { folderId, statusId } = {}) {
  if (!folderId || statusId == null) return
  const current = Array.isArray(state.statusesByFolder[folderId])
    ? state.statusesByFolder[folderId]
    : []
  const next = current.filter(function (s) {
    return s && s.id !== statusId
  })
  Vue.set(state.statusesByFolder, folderId, sortStatusesByPosition(next))
}

// Builds the reordered status list from `orderedIds`, appending any statuses
// not present in the id list at the tail (defensive against partial payloads).
// Reorders the folder's statuses to match `orderedIds` and rewrites each
// `position` to its index (0..N-1). Ordering logic lives in
// `@modules/tarefas/utils/statusOrdering`.
export function SET_FOLDER_STATUSES_ORDER(state, { folderId, orderedIds } = {}) {
  if (!folderId || !Array.isArray(orderedIds)) return
  const current = Array.isArray(state.statusesByFolder[folderId])
    ? state.statusesByFolder[folderId]
    : []
  Vue.set(state.statusesByFolder, folderId, reorderStatusList(current, orderedIds))
}

export function SET_TAGS_LAST_UPDATE(state, ts) {
  state.tagsLastUpdate = ts
}

// Splices a node back into its parent list with an updated `tags` array so
// Vue 2 reactivity picks up the change without `$set`.
function _replaceWithTags(list, idx, node, nextTags) {
  list.splice(idx, 1, { ...node, tags: nextTags })
}

function _replaceParentRef(list, idx, node) {
  list.splice(idx, 1, { ...node })
}

// Walks both top-level tasks and any nested `subtarefas` to push the new tag
// onto the matching task. No-op (returns false) when the task isn't in cache.
function _pushTagOnto(list, taskId, tag) {
  if (!Array.isArray(list) || !list.length) return false
  for (let i = 0; i < list.length; i++) {
    const node = list[i]
    if (!node) continue
    if (node.id === taskId) {
      const current = Array.isArray(node.tags) ? node.tags : []
      const alreadyHas = current.some(function (t) { return t && t.id === tag.id })
      if (!alreadyHas) _replaceWithTags(list, i, node, current.concat([tag]))
      return true
    }
    if (_pushTagOnto(node.subtarefas, taskId, tag)) {
      _replaceParentRef(list, i, node)
      return true
    }
  }
  return false
}

function _removeTagFrom(list, taskId, tagId) {
  if (!Array.isArray(list) || !list.length) return false
  for (let i = 0; i < list.length; i++) {
    const node = list[i]
    if (!node) continue
    if (node.id === taskId) {
      const current = Array.isArray(node.tags) ? node.tags : []
      const filtered = current.filter(function (t) { return !t || t.id !== tagId })
      if (filtered.length !== current.length) _replaceWithTags(list, i, node, filtered)
      return true
    }
    if (_removeTagFrom(node.subtarefas, taskId, tagId)) {
      _replaceParentRef(list, i, node)
      return true
    }
  }
  return false
}

export function ADD_TAG_TO_TASK(state, { taskId, tag }) {
  if (!taskId || !tag) return
  _pushTagOnto(state.tasks, taskId, tag)
  if (state.taskDetail && state.taskDetail.id === taskId) {
    const currentDetailTags = Array.isArray(state.taskDetail.tags) ? state.taskDetail.tags : []
    const alreadyHas = currentDetailTags.some(function (t) {
      return t && t.id === tag.id
    })
    if (!alreadyHas) {
      state.taskDetail = {
        ...state.taskDetail,
        tags: currentDetailTags.concat([tag]),
      }
    }
  }
}

export function REMOVE_TAG_FROM_TASK(state, { taskId, tagId }) {
  if (!taskId || !tagId) return
  _removeTagFrom(state.tasks, taskId, tagId)
  if (state.taskDetail && state.taskDetail.id === taskId) {
    const currentDetailTags = Array.isArray(state.taskDetail.tags) ? state.taskDetail.tags : []
    const filtered = currentDetailTags.filter(function (t) {
      return !t || t.id !== tagId
    })
    if (filtered.length !== currentDetailTags.length) {
      state.taskDetail = { ...state.taskDetail, tags: filtered }
    }
  }
}

// Devolve o array de irmãos que contém `folderId` — a raiz ou o `subpastas`
// de alguma pasta. `undefined` quando o id não está na árvore.
function _siblingListOf(list, folderId) {
  if (!Array.isArray(list) || !list.length) return undefined
  for (const node of list) {
    if (node && node.id === folderId) return list
  }
  for (const node of list) {
    if (!node) continue
    const found = _siblingListOf(node.subpastas, folderId)
    if (found) return found
  }
  return undefined
}

// O backend só aceita reordenar pastas de um mesmo nível, então basta achar a
// lista de irmãos que contém o primeiro id e reordenar ali. Sem isso, um
// payload de subpastas não casaria com nenhuma raiz e `state.folders` viraria
// `[]` — a árvore inteira sumiria da sidebar.
export function REORDER_FOLDERS(state, orderedIds) {
  if (!Array.isArray(orderedIds) || !orderedIds.length) return
  const siblings = _siblingListOf(state.folders, orderedIds[0])
  if (!siblings) return

  const folderMap = {}
  for (const folder of siblings) {
    if (folder) folderMap[folder.id] = folder
  }
  const reordered = []
  for (const id of orderedIds) {
    if (folderMap[id]) {
      reordered.push(folderMap[id])
    }
  }
  siblings.splice(0, siblings.length, ...reordered)
}

export function REORDER_SPRINTS(state, { folderId, orderedIds }) {
  const folder = _findFolderNode(state.folders, folderId)
  if (!folder || !folder.sprints) return

  const sprintMap = {}
  for (const sprint of folder.sprints) {
    sprintMap[sprint.id] = sprint
  }
  const reordered = []
  for (const id of orderedIds) {
    if (sprintMap[id]) {
      reordered.push(sprintMap[id])
    }
  }
  folder.sprints = reordered
}

export function REORDER_TASKS(state, { orderedIds }) {
  for (let i = 0; i < orderedIds.length; i++) {
    const task = state.tasks.find(function (t) {
      return t.id === orderedIds[i]
    })
    if (task) {
      task.sortOrder = i
    }
  }
}

export function SET_ASSIGNEES(state, assignees) {
  state.assignees = assignees || { professionals: [], clients: [] }
}

export function SET_ASSIGNEES_LAST_UPDATE(state, ts) {
  state.assigneesLastUpdate = ts
}

export function SET_ASSIGNEES_LOADING(state, val) {
  state.assigneesLoading = !!val
}

export function SET_CUSTOM_FIELDS(state, customFields) {
  state.customFields = customFields || []
}

export function UPSERT_CUSTOM_FIELD(state, field) {
  if (!field) return
  const idx = state.customFields.findIndex(function (f) {
    return f.id === field.id
  })
  if (idx === -1) {
    state.customFields.push(field)
  } else {
    state.customFields.splice(idx, 1, field)
  }
}

export function REMOVE_CUSTOM_FIELD(state, fieldId) {
  state.customFields = state.customFields.filter(function (f) {
    return f.id !== fieldId
  })
}

export function SET_CURRENT_TASK_DETAIL(state, detail) {
  state.taskDetail = detail || null
}

// --- List column preference slice ----------------------------------------
// Replaces `state.listPreference` accepting *partial payload* (mesmo padrão
// de SET_BOARD_FILTERS): caller pode flipar `loading` sem tocar `columns`.
// `columns` é coagido defensivamente para array-ou-null (null = sem
// preferência → default do registry no getter `visibleListColumns`).
export function SET_LIST_PREFERENCE(state, payload) {
  const safe = payload && typeof payload === 'object' ? payload : {}
  const current = state.listPreference || { sprintId: null, columns: null, loading: false }
  let columns = current.columns
  if (Object.hasOwn(safe, 'columns')) {
    columns = Array.isArray(safe.columns) ? safe.columns : null
  }
  state.listPreference = {
    sprintId: Object.hasOwn(safe, 'sprintId') ? safe.sprintId : current.sprintId,
    columns: columns,
    loading: Object.hasOwn(safe, 'loading') ? !!safe.loading : current.loading,
  }
}

// --- Overview slice -----------------------------------------------------
// All four mutations replace the slot atomically and stamp `lastUpdate`
// for the TTL guard in `actions.load*`. Defensive coercion keeps malformed
// backend payloads (null/missing keys) from breaking consumers downstream.

export function SET_OVERVIEW_OVERDUE_TASKS(state, list) {
  state.overview.overdueTasks = Array.isArray(list) ? list : []
  state.overview.lastUpdate.overdueTasks = Date.now()
}

export function SET_OVERVIEW_WEEKLY_DUE(state, list) {
  state.overview.weeklyDue = Array.isArray(list) ? list : []
  state.overview.lastUpdate.weeklyDue = Date.now()
}

export function SET_OVERVIEW_WEEKLY_DONE(state, list) {
  state.overview.weeklyDone = Array.isArray(list) ? list : []
  state.overview.lastUpdate.weeklyDone = Date.now()
}

export function SET_OVERVIEW_MEMBER_DISTRIBUTION(state, payload) {
  const safe = payload && typeof payload === 'object'
    ? {
        members: Array.isArray(payload.members) ? payload.members : [],
        hasOverdue: !!payload.hasOverdue,
      }
    : { members: [], hasOverdue: false }
  state.overview.memberDistribution = safe
  state.overview.lastUpdate.memberDistribution = Date.now()
}

export function SET_OVERVIEW_CLIENT_DISTRIBUTION(state, payload) {
  const safe = payload && typeof payload === 'object'
    ? {
        members: Array.isArray(payload.members) ? payload.members : [],
        hasOverdue: !!payload.hasOverdue,
      }
    : { members: [], hasOverdue: false }
  state.overview.clientDistribution = safe
  state.overview.lastUpdate.clientDistribution = Date.now()
}

export function SET_OVERVIEW_CHART_MONTHLY(state, payload) {
  const safe = payload && typeof payload === 'object'
    ? {
        categories: Array.isArray(payload.categories) ? payload.categories : [],
        created: Array.isArray(payload.created) ? payload.created : [],
        resolved: Array.isArray(payload.resolved) ? payload.resolved : [],
      }
    : { categories: [], created: [], resolved: [] }
  state.overview.chartMonthly = safe
  state.overview.lastUpdate.chartMonthly = Date.now()
}

export function SET_OVERVIEW_LOADING(state, payload) {
  if (!payload || typeof payload !== 'object') return
  const slot = payload.slot
  const value = payload.value
  if (slot && Object.hasOwn(state.overview.loading, slot)) {
    state.overview.loading[slot] = !!value
  }
}

// Replaces the date filter atomically. Empty/falsy values become null so the
// getter can short-circuit when no filter is active.
export function SET_DATE_FILTER(state, payload) {
  const safe = payload && typeof payload === 'object' ? payload : {}
  state.dateFilter = {
    startDate: safe.startDate || null,
    endDate: safe.endDate || null,
  }
}

// --- Board / Overview server-side filter slice ----------------------------
// Mutations aceitam *partial payload* — caller pode mudar uma dimensão
// sem resetar as outras. Os 3 ids (responsavelIds / clientIds / tagIds)
// são arrays multi-select; normalizados defensivamente para evitar null
// quando o picker emite "limpar".

function _normalizeIdArray(value) {
  if (!Array.isArray(value)) return []
  return value.map(function (t) {
    if (t == null) return null
    // Pickers podem emitir id bruto OU objeto { id, name/value }.
    if (typeof t === 'object') return t.id
    return t
  }).filter(function (id) { return id != null && id !== '' })
}

export function SET_BOARD_FILTERS(state, partial) {
  const safe = partial && typeof partial === 'object' ? partial : {}
  const current = state.boardFilters || { responsavelIds: [], clientIds: [], tagIds: [] }
  state.boardFilters = {
    responsavelIds:
      Object.hasOwn(safe, 'responsavelIds') ? _normalizeIdArray(safe.responsavelIds) : current.responsavelIds,
    clientIds:
      Object.hasOwn(safe, 'clientIds') ? _normalizeIdArray(safe.clientIds) : current.clientIds,
    tagIds:
      Object.hasOwn(safe, 'tagIds') ? _normalizeIdArray(safe.tagIds) : current.tagIds,
  }
}

export function CLEAR_BOARD_FILTERS(state) {
  state.boardFilters = { responsavelIds: [], clientIds: [], tagIds: [] }
}

export function SET_OVERVIEW_FILTERS(state, partial) {
  const safe = partial && typeof partial === 'object' ? partial : {}
  const current = state.overviewFilters || {
    startDate: null, endDate: null, responsavelIds: [], clientIds: [],
  }
  state.overviewFilters = {
    startDate:
      Object.hasOwn(safe, 'startDate') ? (safe.startDate || null) : current.startDate,
    endDate:
      Object.hasOwn(safe, 'endDate') ? (safe.endDate || null) : current.endDate,
    responsavelIds:
      Object.hasOwn(safe, 'responsavelIds') ? _normalizeIdArray(safe.responsavelIds) : current.responsavelIds,
    clientIds:
      Object.hasOwn(safe, 'clientIds') ? _normalizeIdArray(safe.clientIds) : current.clientIds,
  }
}

export function CLEAR_OVERVIEW_FILTERS(state) {
  state.overviewFilters = {
    startDate: null, endDate: null, responsavelIds: [], clientIds: [],
  }
}

// Duplicate View / Espelhamento de tarefa
export function SET_TASK_MIRRORS(state, { taskId, mirrors }) {
  if (taskId == null) return
  Vue.set(state.taskMirrors, taskId, mirrors || [])
}

export function SET_TASK_MIRRORS_LOADING(state, { taskId, loading }) {
  if (taskId == null) return
  Vue.set(state.taskMirrorsLoading, taskId, !!loading)
}

// --- Agenda slice ---------------------------------------------------------
// Setters puros do slice isolado da Agenda (ver comentário em `state.js`).
// A action `loadAgendaTasks` (store/actions.js) é a única chamadora — nenhum
// destes commits substitui `SET_TASKS`/`SET_DATE_FILTER` do board.

export function SET_AGENDA_TASKS(state, tasks) {
  state.agendaTasks = Array.isArray(tasks) ? tasks : []
}

export function SET_AGENDA_WINDOW(state, window) {
  state.agendaWindow = window && typeof window === 'object' ? window : null
}

export function SET_AGENDA_LOADING(state, value) {
  state.agendaLoading = !!value
}

// `error` é `{ message }` (ou `null` para limpar). Diferente de
// `SET_TASKS_LOAD_ERROR`/`SET_TASKS`, esta mutation NUNCA zera `agendaTasks`
// — a Agenda mantém a última janela válida renderizada numa falha.
export function SET_AGENDA_ERROR(state, error) {
  state.agendaError = error && typeof error === 'object' ? error : null
}
